import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../../api/ghost';

function AuthorPage() {
  const [author, setAuthor] = useState(null);
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { slug } = useParams();

  useEffect(() => {
    setIsLoading(true);
    setError(null);

    // First fetch the author data
    api.authors
      .read({ slug })
      .then((authorData) => {
        console.log('Author data:', authorData);
        setAuthor(authorData);
        
        // Then fetch posts by this author
        return api.posts.browse({
          filter: `primary_author:${authorData.slug}`,
          include: ['tags'],
          limit: 10
        });
      })
      .then((postsData) => {
        console.log('Author posts:', postsData);
        setPosts(postsData);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Error loading author data:', err);
        setError('Author not found or could not be loaded');
        setIsLoading(false);
      });
  }, [slug]);

  if (isLoading) return (
    <div className="w-full overflow-x-hidden pt-16 bg-black">
      <div className="max-w-4xl mx-auto px-8 py-16 mt-20 text-neutral-300">
        Loading author...
      </div>
    </div>
  );
  
  if (error) return (
    <div className="w-full overflow-x-hidden pt-16 bg-black">
      <div className="max-w-4xl mx-auto px-8 py-16 mt-20 text-[#00CF9D]">
        {error}
      </div>
    </div>
  );
  
  if (!author) return (
    <div className="w-full overflow-x-hidden pt-16 bg-black">
      <div className="max-w-4xl mx-auto px-8 py-16 mt-20 text-neutral-300">
        Author not found
      </div>
    </div>
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getReadingTime = (minutes) => {
    return `${minutes} min read`;
  };

  return (
    <main className="w-full overflow-x-hidden pt-16 bg-black">
      <div className="max-w-6xl mx-auto px-8 py-16 mt-12">
        <Link 
          to="/blog" 
          className="text-[#00CF9D] hover:underline font-mono text-sm flex items-center mb-8"
        >
          ← Back to blog
        </Link>

        {/* Author Header */}
        <motion.div 
          className="mb-16"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {author.cover_image && (
            <div className="w-full h-64 md:h-96 rounded-xl overflow-hidden mb-8">
              <img 
                src={author.cover_image} 
                alt={`Cover for ${author.name}`}
                className="w-full h-full object-cover" 
              />
            </div>
          )}

          <div className="flex flex-col md:flex-row items-center text-center md:text-left gap-6 md:gap-10">
            <div className="flex-shrink-0">
              {author.profile_image ? (
                <img 
                  src={author.profile_image} 
                  alt={author.name}
                  className="w-28 h-28 rounded-full object-cover border-4 border-[#00CF9D]/30" 
                />
              ) : (
                <div className="w-28 h-28 rounded-full bg-neutral-800 flex items-center justify-center border-4 border-[#00CF9D]/30">
                  <span className="text-neutral-400 text-3xl font-mono">
                    {author.name.charAt(0)}
                  </span>
                </div>
              )}
            </div>

            <div className="flex-1">
              <h1 className="text-3xl md:text-4xl font-mono font-bold mb-4 text-[#DBDBDB]">{author.name}</h1>
              
              {author.location && (
                <p className="text-neutral-400 mb-3">{author.location}</p>
              )}
              
              {author.bio && (
                <p className="text-neutral-300 mb-5 max-w-2xl">{author.bio}</p>
              )}
              
              <div className="flex flex-wrap justify-center md:justify-start gap-4 mt-3">
                {author.website && (
                  <a 
                    href={author.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-[#00CF9D] hover:underline flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-1">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="2" y1="12" x2="22" y2="12"></line>
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                    </svg>
                    Website
                  </a>
                )}
                
                {author.twitter && (
                  <a 
                    href={`https://twitter.com/${author.twitter.replace('@', '')}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-[#00CF9D] hover:underline flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-1">
                      <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path>
                    </svg>
                    Twitter
                  </a>
                )}
                
                {author.facebook && (
                  <a 
                    href={`https://facebook.com/${author.facebook}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-[#00CF9D] hover:underline flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mr-1">
                      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                    </svg>
                    Facebook
                  </a>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Author's Posts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h2 className="text-2xl font-mono font-bold mb-8 text-[#DBDBDB]">
            Articles by {author.name}
            {posts.length > 0 && <span className="text-neutral-400 ml-2">({posts.length})</span>}
          </h2>

          {posts.length === 0 ? (
            <div className="text-neutral-300 py-8">
              <p>No posts found for this author.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map(post => (
                <Link to={`/blog/${post.slug}`} className="block" key={post.id}>
                  <div className="bg-neutral-900 border border-neutral-800 rounded-md overflow-hidden hover:border-[#00CF9D] transition-colors duration-300 h-full">
                    <div className="relative">
                      {post.feature_image ? (
                        <img 
                          src={post.feature_image} 
                          alt={post.feature_image_alt || post.title}
                          className="w-full h-48 object-cover" 
                        />
                      ) : (
                        <div className="w-full h-48 bg-neutral-800 flex items-center justify-center">
                          <span className="text-neutral-400">No image available</span>
                        </div>
                      )}
                      <div className="absolute bottom-2 right-2 bg-black/70 px-2 py-1 rounded text-xs text-white">
                        {getReadingTime(post.reading_time || 5)}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-lg font-mono font-bold mb-2 text-[#DBDBDB] line-clamp-2">{post.title}</h3>
                      
                      <div className="text-xs text-neutral-400 mb-2">
                        {post.published_at && (
                          <time>{formatDate(post.published_at)}</time>
                        )}
                      </div>
                      
                      <p className="text-neutral-300 mb-3 text-sm line-clamp-2">{post.excerpt}</p>
                      
                      <div className="text-sm text-[#00CF9D] hover:underline">
                        Read post →
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </main>
  );
}

export default AuthorPage; 